import { Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import { type FC, Fragment, type ReactNode } from "react";

import { ROUTES } from "~/constants/routing";
import type {
  ContentBlock,
  InlinePart,
  ListItemContent,
  StaticPageDocument,
  StaticPageRouteKey,
  StaticPageSection,
} from "~/feature/staticInfo/types/staticPageContent.types";
import { IntercomButton } from "~/feature/support/IntercomButton";

import {
  slugify,
  StaticPageHeading,
  StaticPageLayout,
  StaticPageList,
  StaticPageNav,
  StaticPageParagraph,
  StaticPageSection as StaticPageSectionUi,
  StaticPageSubheading,
  StaticPageTitle,
  StaticPageTocLink,
} from "./staticPageUi";

const ROUTE_RESOLVERS: Record<
  StaticPageRouteKey,
  () => string
> = {
  main: () => ROUTES.main.createURL(),
};

const renderInlinePart = (part: InlinePart, index: number): ReactNode => {
  if (typeof part === "string") {
    return <Fragment key={index}>{part}</Fragment>;
  }

  if ("bold" in part) {
    return (
      <Typography
        key={index}
        component="span"
        variant="caption"
        sx={{
          fontWeight: 700
        }}
      >
        {part.bold}
      </Typography>
    );
  }

  if ("link" in part) {
    return (
      <Link key={index} href={part.href} color="info">
        {part.link}
      </Link>
    );
  }

  if ("intercom" in part) {
    return <IntercomButton key={index} text={part.intercom} />;
  }

  return (
    <Link key={index} href={ROUTE_RESOLVERS[part.route]()} color="info">
      {part.text}
    </Link>
  );
};

const renderInlineParts = (parts: InlinePart[]): ReactNode =>
  parts.map(renderInlinePart);

const renderListItem = (item: ListItemContent): ReactNode => {
  if (typeof item === "string") {
    return item;
  }

  return <>{renderInlineParts(item.parts)}</>;
};

const renderParagraph = (block: Extract<ContentBlock, { type: "paragraph" }>) => {
  if (block.parts?.length) {
    return (
      <StaticPageParagraph>{renderInlineParts(block.parts)}</StaticPageParagraph>
    );
  }

  return <StaticPageParagraph>{block.text}</StaticPageParagraph>;
};

const renderBlock = (block: ContentBlock, index: number): ReactNode => {
  switch (block.type) {
    case "paragraph":
      return <Fragment key={index}>{renderParagraph(block)}</Fragment>;
    case "list":
      return (
        <StaticPageList
          key={index}
          items={block.items.map((item) => renderListItem(item))}
        />
      );
    case "heading":
      return (
        <StaticPageHeading
          key={index}
          level={block.level}
          title={block.title}
          tone={block.tone}
        />
      );
    case "subheading":
      return <StaticPageSubheading key={index} title={block.title} />;
    case "table":
      return (
        <TableContainer key={index} component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                {block.headers.map((header) => (
                  <TableCell key={header} sx={{ fontWeight: 700 }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {block.rows.map((row) => (
                <TableRow key={row.join("-")}>
                  {row.map((cell) => (
                    <TableCell key={cell}>{cell}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    case "intercom":
      return (
        <StaticPageParagraph key={index}>
          <IntercomButton text={block.text} />
        </StaticPageParagraph>
      );
    case "group":
      return (
        <Stack key={index} sx={{
          gap: 1
        }}>
          {block.blocks.map((child, childIndex) =>
            renderBlock(child, childIndex),
          )}
        </Stack>
      );
    default:
      return null;
  }
};

const getSectionId = (section: StaticPageSection): string => {
  if (section.id) {
    return section.id;
  }

  if (section.heading) {
    return slugify(section.heading.title);
  }

  const firstHeading = section.blocks.find(
    (block): block is Extract<ContentBlock, { type: "heading" }> =>
      block.type === "heading",
  );

  return firstHeading ? slugify(firstHeading.title) : `section-${section.blocks.length}`;
};

const renderSection = (section: StaticPageSection, index: number) => {
  const sectionId = getSectionId(section);

  return (
    <StaticPageSectionUi key={sectionId ?? index} id={sectionId}>
      {section.heading ? (
        <StaticPageHeading
          level={section.heading.level}
          title={section.heading.title}
          tone={section.heading.tone}
        />
      ) : null}
      {section.blocks.map((block, blockIndex) => renderBlock(block, blockIndex))}
    </StaticPageSectionUi>
  );
};

type StaticPageRendererProps = {
  content: StaticPageDocument;
};

export const StaticPageRenderer: FC<StaticPageRendererProps> = ({ content }) => {
  return (
    <StaticPageLayout>
      <StaticPageTitle>{content.title}</StaticPageTitle>

      {content.preamble?.map((block, index) => renderBlock(block, index))}

      {content.tableOfContents ? (
        <StaticPageNav>
          <StaticPageSubheading
            title={content.tableOfContents.label ?? "On this page"}
          />
          <StaticPageList
            items={content.tableOfContents.items.map((title) => (
              <StaticPageTocLink
                key={title}
                href={`#${slugify(title)}`}
              >
                {title}
              </StaticPageTocLink>
            ))}
          />
        </StaticPageNav>
      ) : null}

      {content.sections.map((section, index) => renderSection(section, index))}
    </StaticPageLayout>
  );
};
